import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Trash2, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const Wishlist = () => {
  const { t, i18n } = useTranslation();
  const { wishlist, removeFromWishlist, addToCart } = useApp();
  const lang = i18n.language;

  const handleMoveToCart = async (product) => {
    await addToCart(product.id, 1, product.colors?.[0] || null);
    await removeFromWishlist(product.id);
    toast.success(t('wishlist.moveToCart') + ' ✓', {
      description: product.name[lang] || product.name.en,
      style: { background: '#1A1A1A', border: '1px solid #FF0099', color: '#fff' }
    });
  };

  const handleRemove = async (productId, productName) => {
    await removeFromWishlist(productId);
    toast.success(t('shop.removeFromWishlist') + ' ✓', {
      description: productName,
      style: { background: '#1A1A1A', border: '1px solid #333', color: '#fff' }
    });
  };

  if (wishlist.items.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4" data-testid="wishlist-empty">
        <Heart className="w-20 h-20 text-[#333333] mb-6" />
        <h1 className="font-heading text-2xl sm:text-3xl text-white font-bold mb-4">
          {t('wishlist.empty')}
        </h1>
        <Link to="/shop">
          <Button className="btn-primary">
            Discover Products
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="wishlist-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-8">
          {t('wishlist.title')}
          <span className="text-[#FF0099] ml-2">({wishlist.items.length})</span>
        </h1>

        {/* Wishlist Items */}
        <div className="space-y-4">
          {wishlist.items.map((product) => (
            <div 
              key={product.id}
              className="bg-[#1A1A1A] border border-[#333333] p-4 flex gap-4"
              data-testid={`wishlist-item-${product.id}`}
            >
              {/* Image */}
              <Link to={`/product/${product.id}`} className="shrink-0">
                <img
                  src={product.image_url}
                  alt={product.name?.[lang] || product.name?.en}
                  className="w-24 h-24 object-cover"
                />
              </Link>

              {/* Info */}
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <Link to={`/product/${product.id}`}>
                    <h3 className="font-heading text-white font-bold text-sm uppercase tracking-wider hover:text-[#FF0099] transition-colors">
                      {product.name?.[lang] || product.name?.en}
                    </h3>
                  </Link>
                  <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-2">
                    {product.description?.[lang] || product.description?.en}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-2">
                  <p className="text-[#FF0099] font-bold text-xl">
                    €{product.price?.toFixed(2)}
                  </p>
                  
                  <Button
                    onClick={() => handleMoveToCart(product)}
                    disabled={!product.in_stock}
                    className="btn-primary py-2 px-4 text-sm"
                    data-testid={`move-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {t('wishlist.moveToCart')}
                  </Button>
                </div>
              </div>

              {/* Remove Button */}
              <button
                onClick={() => handleRemove(product.id, product.name?.[lang] || product.name?.en)}
                className="text-[#A3A3A3] hover:text-[#FF0000] transition-colors self-start"
                data-testid={`remove-wishlist-${product.id}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
